import { getCommunicationCategory } from '@bariendo/lib/communication';
import { CommunicationCategory, ConfidentialityLevel, ProviderRole } from '@bariendo/lib/constants';
import {
  AccessPolicyInteraction,
  accessPolicySupportsInteraction,
  getReferenceString,
  MedplumClient,
} from '@medplum/core';
import { Communication, Practitioner, Reference, Resource, ResourceType } from '@medplum/fhirtypes';
import { alert } from './alert';
import { fetchBot } from './services';

/**
 * Whether the signed-in membership's policy lists `resourceType` at all.
 *
 * Medplum refuses a search outright, rather than filtering it, when it touches a type the
 * policy does not list; a `_revinclude` naming such a type is the inferred cause of the blank
 * Patient Access calendar (BAR-2660, staging replay pending). Use this to leave such a join
 * out for roles that cannot read the joined type.
 *
 * Reads the policy `/auth/me` returned, so it is as fresh as the last login or token refresh;
 * a grant changed under a live session is enforced by the server before this catches up. No
 * policy means an unrestricted membership, which only holds once `/auth/me` has resolved:
 * callers run behind App's loading gate, not before it.
 */
export function canSearchResourceType(medplum: MedplumClient, resourceType: ResourceType): boolean {
  const accessPolicy = medplum.getAccessPolicy();
  return !accessPolicy || accessPolicySupportsInteraction(accessPolicy, AccessPolicyInteraction.SEARCH, resourceType);
}

/**
 * Whether the signed-in membership's policy lets it update `resourceType` at all — listed and
 * not `readonly`. Per-resource criteria and `writeConstraint`s are still the server's call.
 * Same freshness and no-policy rules as `canSearchResourceType`.
 */
export function canUpdateResourceType(medplum: MedplumClient, resourceType: ResourceType): boolean {
  const accessPolicy = medplum.getAccessPolicy();
  return !accessPolicy || accessPolicySupportsInteraction(accessPolicy, AccessPolicyInteraction.UPDATE, resourceType);
}

/** Whether the policy lets the membership create `resourceType` at all. Same rules as `canUpdateResourceType`. */
export function canCreateResourceType(medplum: MedplumClient, resourceType: ResourceType): boolean {
  const accessPolicy = medplum.getAccessPolicy();
  return !accessPolicy || accessPolicySupportsInteraction(accessPolicy, AccessPolicyInteraction.CREATE, resourceType);
}

/** Whether the policy lets the membership delete `resourceType` at all. Same rules as `canUpdateResourceType`. */
export function canDeleteResourceType(medplum: MedplumClient, resourceType: ResourceType): boolean {
  const accessPolicy = medplum.getAccessPolicy();
  return !accessPolicy || accessPolicySupportsInteraction(accessPolicy, AccessPolicyInteraction.DELETE, resourceType);
}

export function canEditNote(
  note: Communication,
  currentUser: Practitioner,
  providerRole: ProviderRole | undefined
): boolean {
  const category = getCommunicationCategory(note);
  const isNote = category === CommunicationCategory.TIMELINE_NOTE || category === CommunicationCategory.STANDALONE_NOTE;

  if (!isNote) return false;

  const isAdmin = providerRole === ProviderRole.ADMINISTRATOR;
  const isAuthor = getReferenceString(currentUser) === getReferenceString(note.sender!);

  return isAdmin || isAuthor;
}

export async function changeResourceConfidentiality(
  medplum: MedplumClient,
  {
    resourceRef,
    confidentialityLevel,
  }: {
    resourceRef?: Reference<Resource>;
    confidentialityLevel?: ConfidentialityLevel;
  }
): Promise<unknown> {
  if (!resourceRef) {
    throw new Error('Resource is required');
  }

  try {
    return await fetchBot(medplum, 'change-resource-confidentiality', {
      resourceRef,
      confidentialityLevel: confidentialityLevel,
    });
  } catch (error) {
    alert.error({
      title: "Couldn't update access restriction",
      message: error instanceof Error ? error.message : 'Unknown error',
    });
    throw error;
  }
}
